import { useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ExternalLink, MapPin, ArrowUpRight, BookOpen } from "lucide-react";
import { STATUS_LABELS } from "@/data/apparitions";
import { EASE } from "@/components/landing/Reveal";
import { SourceItem } from "./SourcesPanel";
import { ShrinePrayers } from "./ShrinePrayers";
import { FullStory } from "./FullStory";

export const TABS = [
  { id: "overview", label: "Overview", heading: "The encounter at a glance" },
  { id: "history", label: "Full Story", heading: "The full story" },
  { id: "themes", label: "Themes", heading: "Spiritual themes" },
  { id: "discernment", label: "Church discernment", heading: "Church discernment and what it means" },
  { id: "shrine", label: "Shrine", heading: "The shrine today" },
  { id: "prayer", label: "Prayer", heading: "A prayer and Scripture reflection" },
  { id: "sources", label: "Sources", heading: "Sources and further reading" },
];

const Paras = ({ items, prefix = "apparition-text" }) => (
  <div className="max-w-[70ch] space-y-6 text-base leading-8 text-charcoal/90 sm:text-lg">
    {items.map((p, i) => (
      <p key={i} data-testid={`${prefix}-${i}`} className={i === 0 ? "first-letter:float-left first-letter:mr-2 first-letter:font-serif first-letter:text-6xl first-letter:font-semibold first-letter:leading-[0.8] first-letter:text-marian" : ""}>
        {p}
      </p>
    ))}
  </div>
);

function TabBody({ entry, tab, onSelect }) {
  switch (tab) {
    case "overview":
      return (
        <>
          <Paras items={entry.overview} prefix="overview-paragraph" />
          {entry.story.overviewSourceIds?.length > 0 && <div data-testid="overview-quotation-sources" className="mt-5 border-l-2 border-gold/60 pl-4 text-xs leading-relaxed text-slateblue">
            <p data-testid="overview-quotation-sources-label">Quoted wording</p>
            {entry.story.overviewSourceIds.map((id) => {
              const source = entry.story.sources.find((item) => item.id === id);
              return <a key={id} href={source.url} target="_blank" rel="noopener noreferrer" data-testid={`overview-quotation-source-${id}`} className="mt-2 block text-marian underline underline-offset-4 transition-colors hover:text-gold">{source.label}</a>;
            })}
          </div>}
          <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="overview-story-invitation">
            <button type="button" onClick={() => onSelect("history")} data-testid="read-full-story" className="inline-flex items-center gap-2 rounded-full bg-marian px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-midnight"><BookOpen size={17} aria-hidden="true" />Read the full story<ArrowUpRight size={16} aria-hidden="true" /></button>
            <span data-testid="overview-reading-time" className="text-xs text-slateblue">{entry.story.readingMinutes} min read</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {entry.themes.map((t) => (
              <span key={t} data-testid={`overview-theme-${entry.themes.indexOf(t)}`} className="rounded-full bg-paleblue px-3.5 py-1.5 text-xs font-semibold text-marian">
                {t}
              </span>
            ))}
          </div>
        </>
      );
    case "history":
      return <FullStory entry={entry} onOverview={() => onSelect("overview")} />;
    case "themes":
      return (
        <ol className="space-y-8">
          {entry.themesText.map((t, i) => (
            <li key={t.title} className="grid gap-4 sm:grid-cols-[3.5rem_1fr]">
              <span className="font-serif text-4xl font-semibold text-gold/80">0{i + 1}</span>
              <div>
                <h4 className="font-serif text-2xl font-semibold text-midnight">{t.title}</h4>
                <p className="mt-2 text-base leading-relaxed text-charcoal/90">{t.text}</p>
              </div>
            </li>
          ))}
        </ol>
      );
    case "discernment":
      return (
        <>
          <div data-testid="apparition-discernment-summary" className="border-l-2 border-gold pl-5 font-serif text-xl leading-relaxed text-midnight sm:text-2xl">
            {entry.status}
            <footer className="mt-3 font-mono text-[10px] not-italic uppercase tracking-[0.2em] text-slateblue">
              Editorial summary · {entry.authority}
            </footer>
          </div>
          <div className="mt-8">
            <Paras items={entry.discernment} />
          </div>
          <div className="mt-8 rounded-2xl border border-marian/15 bg-paleblue/60 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-marian">Status labels on this page</p>
            <ul className="mt-3 space-y-2">
              {entry.labels.map((l) => (
                <li key={l} className="flex items-start gap-2 text-sm text-charcoal/90">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                  {STATUS_LABELS[l]}
                </li>
              ))}
            </ul>
            <Link
              to="/editorial-standards#discernment"
              data-testid="discernment-explainer-link"
              className="group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-marian hover:text-midnight"
            >
              How the Church discerns apparitions — read the explanation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </>
      );
    case "shrine":
      return (
        <div className="grid gap-8 md:grid-cols-[1fr_1.2fr]">
          <figure className="overflow-hidden rounded-2xl border border-marian/15">
            <img src={entry.image.url} alt={entry.image.alt} data-testid="shrine-artwork" loading="lazy" className={`aspect-[4/5] w-full ${entry.image.contain ? "object-contain" : "object-cover"} ${entry.image.position || "object-center"}`} />
          </figure>
          <div>
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {entry.shrine.location}
            </p>
            <h4 className="mt-3 font-serif text-2xl font-semibold text-midnight sm:text-3xl">{entry.shrine.name}</h4>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90">{entry.shrine.text}</p>
            <a
              href={entry.shrine.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shrine-website-link"
              className="group mt-6 inline-flex items-center gap-2 rounded-full border border-marian/35 bg-white/70 px-5 py-2.5 text-sm font-semibold text-marian transition-all hover:border-gold hover:text-midnight"
            >
              {entry.shrine.linkLabel || "Official shrine website"}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      );
    case "prayer":
      return (
        <div className="space-y-8">
          <ShrinePrayers entry={entry} />
          <div className="border-l-2 border-gold/60 pl-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-marian" data-testid={`prayer-scripture-ref-${entry.id}`}>{entry.scripture.ref}</p>
            <p data-testid={`prayer-scripture-text-${entry.id}`} className="mt-2 font-serif text-xl italic leading-relaxed text-midnight">{entry.scripture.text}</p>
            {entry.scripture.url && <a href={entry.scripture.url} target="_blank" rel="noreferrer" data-testid={`prayer-scripture-source-${entry.id}`} className="mt-3 inline-block text-xs text-marian underline underline-offset-4 transition-colors hover:text-midnight">Read the passage · RSV-CE</a>}
          </div>
        </div>
      );
    case "sources":
      return (
        <ol className="space-y-3">
          {entry.sources.map((s, i) => (
            <SourceItem key={`${s.url}-${i}`} source={s} index={i} testIdPrefix={`source-tab-${entry.id}`} />
          ))}
        </ol>
      );
    default:
      return null;
  }
}

export default function ApparitionTabs({ entry }) {
  const location = useLocation();
  const navigate = useNavigate();
  const buttons = useRef([]);
  const reducedMotion = useReducedMotion();
  const overviewMinutes = Math.max(1, Math.ceil(entry.overview.join(" ").trim().split(/\s+/).length / 200));
  const hash = location.hash.slice(1);
  const requested = hash === "full-story" ? "history" : hash;
  const active = TABS.some((tab) => tab.id === requested) ? requested : "overview";
  const setActive = (id) => navigate({ pathname: location.pathname, search: location.search, hash: id === "history" ? "#full-story" : `#${id}` }, { replace: true });
  const current = TABS.find((t) => t.id === active);
  const handleKeyDown = (event, index) => {
    const next = event.key === "ArrowRight" ? (index + 1) % TABS.length : event.key === "ArrowLeft" ? (index + TABS.length - 1) % TABS.length : event.key === "Home" ? 0 : event.key === "End" ? TABS.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    setActive(TABS[next].id);
    buttons.current[next]?.focus();
  };

  return (
    <section id={active === "history" && hash !== "history" ? "full-story" : active} className="mx-auto max-w-7xl scroll-mt-20 px-4 pb-8 pt-12 sm:px-8 lg:px-16" data-testid="apparition-tabs">
      <div data-testid="apparition-reading-navigation" className="sticky top-[66px] z-30 -mx-4 border-y border-marian/15 bg-ivory/95 px-4 py-2 backdrop-blur-xl sm:-mx-8 sm:px-8 lg:-mx-16 lg:px-16">
        <div role="tablist" aria-label="Sections" data-testid="apparition-tab-list" className="grid grid-cols-6 gap-1.5 sm:flex sm:flex-wrap sm:items-stretch sm:gap-2">
          {TABS.map((tab, i) => (
            <button
              key={tab.id}
              role="tab"
              id={`apparition-tab-${tab.id}`}
              ref={(button) => { buttons.current[i] = button; }}
              type="button"
              aria-controls={`apparition-panel-${tab.id}`}
              aria-selected={active === tab.id}
              tabIndex={active === tab.id ? 0 : -1}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActive(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, i)}
              className={`relative min-w-0 rounded-md px-2 py-3 text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marian sm:px-3 ${i < 2 ? "col-span-3 min-h-[64px] text-left sm:min-w-[128px]" : "col-span-2 min-h-11"} ${active === tab.id ? "bg-paleblue text-midnight" : "text-slateblue hover:bg-white hover:text-marian"}`}
            >
              <span className={i < 2 ? "block font-serif text-lg font-semibold" : ""}>{tab.label}</span>
              {i < 2 && <span data-testid={`tab-${tab.id}-reading-time`} className="mt-1 block text-[11px] font-normal text-slateblue">{i === 0 ? overviewMinutes : entry.story.readingMinutes} min read</span>}
              {active === tab.id && (
                <motion.span aria-hidden="true" layoutId={`tab-underline-${entry.id}`} className="absolute inset-x-2 bottom-0 h-0.5 bg-gold" transition={{ duration: reducedMotion ? 0 : 0.25, ease: EASE }} />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 grid gap-12 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <motion.article
            key={active}
            role="tabpanel"
            id={`apparition-panel-${active}`}
            aria-labelledby={`apparition-tab-${active}`}
            data-testid={`panel-${active}`}
            tabIndex={0}
            initial={{ opacity: reducedMotion ? 1 : 0, y: reducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.25, ease: EASE }}
            className="min-w-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-8 focus-visible:outline-marian"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              0{TABS.indexOf(current) + 1} · {current.label}
            </p>
            <h2 data-testid="apparition-panel-heading" className="mt-3 font-serif text-3xl font-semibold leading-snug text-midnight sm:text-4xl">{active === "history" ? entry.story.title : current.heading}</h2>
            <div className="mt-8">
              <TabBody entry={entry} tab={active} onSelect={setActive} />
            </div>
          </motion.article>

        <aside className="space-y-5 lg:sticky lg:top-48 lg:self-start">
          <div data-testid="apparition-status-summary" className="rounded-2xl border border-marian/15 bg-white/80 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Church status</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {entry.labels.map((l) => (
                <span key={l} data-testid={`aside-status-${l}`} className="rounded-full bg-paleblue px-3 py-1 text-[11px] font-semibold text-marian">
                  {STATUS_LABELS[l]}
                </span>
              ))}
            </div>
            <p data-testid="aside-status-authority" className="mt-4 text-xs leading-relaxed text-slateblue">{entry.authority}</p>
          </div>
          <div className="rounded-2xl border border-marian/15 bg-paleblue/60 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-marian">The shrine</p>
            <p className="mt-2 font-serif text-lg font-semibold leading-snug text-midnight">{entry.shrine.name}</p>
            <p className="mt-1 text-xs text-slateblue">{entry.shrine.location}</p>
            <a href={entry.shrine.website} target="_blank" rel="noopener noreferrer" data-testid="aside-shrine-link" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-marian hover:text-midnight">
              {entry.shrine.linkLabel || "Visit website"} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="rounded-2xl border border-dashed border-marian/25 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slateblue">Feast</p>
            <p className="mt-2 font-serif text-lg font-semibold text-midnight">{entry.feast}</p>
            <Link to="/calendar" className="mt-2 inline-block text-xs font-semibold text-marian hover:text-midnight" data-testid="aside-calendar-link">
              See the Marian calendar →
            </Link>
          </div>
        </aside>
      </div>
    </section>
  );
}
